/**
 * الترجمة العربية لكل قيمة تعدادية + خريطة الحالة إلى اللون.
 * المرجع: القسم 04 من دليل الهوية — «خريطة حاالت النظام ← الألوان (ملزمة للمطور والمصمم)».
 * قاعدة إلزامية: شارة الحالة = لون + أيقونة + نص معاً.
 */
import type {
  CallRecordingStatus,
  ConsentStatus, ConsentType, Country, CredentialStatus, EffectiveRole, ExceptionRequestStatus,
  Gender, GoalResultStatus, GoalStatus, GuardianLinkStatus, OutboxStatus, PlanStatus, PlanType,
  PreparationStatus, RecordingAccessStatus, RecordingStatus, SessionLocation, SessionStatus, IntegrationTransport,
  ReportStatus, ReportKind, ReferralTargetTrack, ReferralSource, ReferralStatus, ObservationDomain, ObservationLevel,
  RuleSeverity, RuleAction, RuleKind, AlertStatus, IndicatorKind, SeatRequestStatus,
  CounselingPlanStatus, CounselingSessionStatus, CounselingSessionMode, RecommendationAudience,
  GuardianRelationship, IdentityDocType, InstitutionStatus, InstitutionType, InvitationStatus,
  InvitationTargetType, MembershipGrant, MembershipRole, MembershipStatus, ProfessionalKind,
  ProfessionalVerificationStatus, Track, UserStatus, LinkRequestStatus, GuardianDocType,
  StudentFileKind, StudentFileSpan, StudentFileField, FollowupResult, AssistanceType, RequiredFieldEntity, NewsKind,
  ClientStatus, CallMode, ConsultationStatus, ConsultationTrigger,
  WalletOwnerKind, WalletEntryKind, PayoutStatus, ComplaintStatus,
  ProviderApplicationStatus, AcademyStatus, AcademyMemberStatus, AcademyPostAccess, AcademyPostStatus,
  CourseStatus, CourseDelivery, CourseAudience, CourseCertificate, EnrollmentStatusC,
} from './types';

export type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

export const userStatusLabel: Record<UserStatus, string> = {
  ACTIVE: 'نشط', SUSPENDED: 'موقوف مؤقتاً', BLOCKED: 'محظور', ON_LEAVE: 'في إجازة',
  RESIGNED: 'مستقيل', CONTRACT_ENDED: 'منتهي العقد', INACTIVE: 'غير نشط',
  ARCHIVED: 'مؤرشف', REACTIVATED: 'أُعيد تفعيله',
};
export const userStatusTone: Record<UserStatus, Tone> = {
  ACTIVE: 'success', REACTIVATED: 'success',
  ON_LEAVE: 'warning', SUSPENDED: 'warning', INACTIVE: 'warning',
  BLOCKED: 'danger',
  RESIGNED: 'neutral', CONTRACT_ENDED: 'neutral', ARCHIVED: 'neutral',
};
/** الحالات التي تسمح بتسجيل الدخول — مطابقة لـ LOGINABLE_STATUSES في الخادم */
export const LOGINABLE_STATUSES: UserStatus[] = ['ACTIVE', 'REACTIVATED'];

export const roleLabel: Record<EffectiveRole, string> = {
  PLATFORM_ADMIN: 'إدارة المنصة',
  PLATFORM_SUPPORT: 'قسم خدمة المستفيدين',
  PLATFORM_ACADEMIES: 'قسم الأكاديميات والدورات',
  PLATFORM_INSTITUTIONS: 'قسم المراكز والمدارس',
  PLATFORM_FINANCE: 'القسم المالي',
  INSTITUTION_ADMIN: 'إدارة المؤسسة',
  INTERNAL_SUPERVISOR: 'مشرف داخلي',
  SPECIALIST: 'أخصائي',
  COUNSELOR: 'مرشد طلابي',
  TEACHER: 'معلم',
  PARENT: 'ولي أمر',
  STUDENT: 'طالب',
  PROFESSIONAL: 'حساب مهني',
  REGULATORY_SUPERVISOR: 'مشرف رقابي',
  CLIENT: 'مستفيد',
  OFFICE_STAFF: 'موظف إداري',
  INSTITUTION_DELEGATE: 'مفوَّض عن الإدارة',
  GRANT_ATTENDANCE: 'صلاحية الحضور والغياب',
  GRANT_CLASS_RECORDING: 'صلاحية تسجيل الحصة',
};
/** إدارةُ المنصة وأقسامُها (0066 — 27 سبتمبر 2026) */
export const platformRoleLabel: Record<string, string> = {
  PLATFORM_ADMIN: 'إدارة المنصة',
  PLATFORM_SUPPORT: 'قسم خدمة المستفيدين',
  PLATFORM_ACADEMIES: 'قسم الأكاديميات والدورات',
  PLATFORM_INSTITUTIONS: 'قسم المراكز والمدارس',
  PLATFORM_FINANCE: 'القسم المالي',
};

export const membershipRoleLabel: Record<MembershipRole, string> = {
  INSTITUTION_ADMIN: 'إدارة المؤسسة', INTERNAL_SUPERVISOR: 'مشرف داخلي',
  SPECIALIST: 'أخصائي', COUNSELOR: 'مرشد طلابي', TEACHER: 'معلم', OFFICE_STAFF: 'موظف إداري',
};
/** الصلاحياتُ الممنوحة فردياً (0078) — اسمٌ وجملةٌ تقول ما تفتحه */
export const membershipGrantLabel: Record<MembershipGrant, { label: string; hint: string }> = {
  ATTENDANCE: { label: 'الحضور والغياب', hint: 'يسجّل حضورَ كلِّ الطلبة الملتحقين ويصحّحه، لا طلبته وحدهم.' },
  CLASS_RECORDING: { label: 'تسجيل الحصة', hint: 'للمعلّم: يبدأ حصّةَ البثّ مسجَّلةً — يُضغط التسجيلُ ويُحفظ أربعة أيام على خادم المكالمات ثم يُحذف تلقائياً.' },
};
export const trackLabel: Record<Track, string> = { SPECIAL_EDUCATION: 'مسار التربية الخاصة', COUNSELING: 'مسار الإرشاد' };
export const membershipStatusLabel: Record<MembershipStatus, string> = { ACTIVE: 'ارتباط نشط', ENDED: 'ارتباط منتهٍ' };

export const institutionTypeLabel: Record<InstitutionType, string> = { CENTER: 'مركز', SCHOOL: 'مدرسة' };
export const institutionStatusLabel: Record<InstitutionStatus, string> = { ACTIVE: 'نشطة', SUSPENDED: 'موقوفة', ARCHIVED: 'مؤرشفة' };
export const institutionStatusTone: Record<InstitutionStatus, Tone> = { ACTIVE: 'success', SUSPENDED: 'warning', ARCHIVED: 'neutral' };
// الإطلاقُ في الأردن وحده — القيمةُ الأخرى في النوع لبياناتٍ قديمة لا تُعرض باسمها
export const countryLabel: Record<Country, string> = { SA: '—', JO: 'الأردن' };

export const idDocTypeLabel: Record<IdentityDocType, string> = {
  NATIONAL_ID: 'هوية وطنية', BIRTH_CERTIFICATE: 'شهادة ميلاد', IQAMA: 'إقامة', PASSPORT: 'جواز سفر',
};
export const genderLabel: Record<Gender, string> = { MALE: 'ذكر', FEMALE: 'أنثى' };

export const relationshipLabel: Record<GuardianRelationship, string> = {
  FATHER: 'الأب', MOTHER: 'الأم', LEGAL_GUARDIAN: 'وصي قانوني', OTHER: 'صلة أخرى',
};
export const guardianLinkStatusLabel: Record<GuardianLinkStatus, string> = {
  INVITED: 'دعوة معلّقة', ACTIVE: 'مفعَّل', REVOKED: 'ملغى',
};
export const guardianLinkStatusTone: Record<GuardianLinkStatus, Tone> = { INVITED: 'warning', ACTIVE: 'success', REVOKED: 'neutral' };

export const consentTypeLabel: Record<ConsentType, string> = {
  RECORDING: 'تسجيل الجلسات',
  AI_ANALYSIS: 'التحليل بالذكاء الاصطناعي',
  SHARING: 'مشاركة البيانات مع جهة أخرى',
  AI_TRAINING_RETENTION: 'الاحتفاظ للتدريب والتطوير',
  PHOTO: 'صورة الطالب',
  CLASSROOM_BROADCAST: 'تسجيل الحصص الصفية',
};
export const consentTypeHint: Record<ConsentType, string> = {
  RECORDING: 'تسجيل صوت أو فيديو الجلسة داخل المنصة — بدونها لا تبدأ أي جلسة مسجَّلة (BR-CONSENT-001).',
  AI_ANALYSIS: 'تحليل محتوى الجلسة آلياً لاستخراج المؤشرات ومسودات التقارير.',
  SHARING: 'مشاركة ملف الطفل أو مخرجاته مع مؤسسة أو جهة محدَّدة.',
  AI_TRAINING_RETENTION: 'الاحتفاظ ببيانات مجهَّلة الهوية لتحسين النماذج — اختيارية دائماً.',
  PHOTO: 'صورة الطالب الشخصية داخل ملفه — لا تُرفع ولا تُعرض بلا موافقة سارية، وسحبها يحجب الصورة في اللحظة نفسها (PHOTO-001).',
  CLASSROOM_BROADCAST: 'تسجيل حصص الصف التي يكون فيها الطفل — تُسجَّل ولا تُبثّ مباشرة، ويُحفظ التسجيل أربعة أيام ثم يُحذف.',
};
export const consentStatusLabel: Record<ConsentStatus, string> = {
  PENDING_SIGNATURE: 'بانتظار التوقيع', ACTIVE: 'سارية', EXPIRED: 'منتهية',
  REVOKED: 'مسحوبة', REPLACED: 'استُبدلت بنسخة أحدث',
};
export const consentStatusTone: Record<ConsentStatus, Tone> = {
  PENDING_SIGNATURE: 'warning', ACTIVE: 'success', EXPIRED: 'warning', REVOKED: 'danger', REPLACED: 'neutral',
};

export const invitationStatusLabel: Record<InvitationStatus, string> = {
  PENDING: 'معلّقة', ACCEPTED: 'مقبولة', REJECTED: 'مرفوضة',
  CLARIFICATION_REQUESTED: 'طُلب توضيح', EXPIRED: 'منتهية', CANCELLED: 'ملغاة',
};
export const invitationStatusTone: Record<InvitationStatus, Tone> = {
  PENDING: 'brand', ACCEPTED: 'success', REJECTED: 'danger',
  CLARIFICATION_REQUESTED: 'warning', EXPIRED: 'neutral', CANCELLED: 'neutral',
};
export const invitationTargetLabel: Record<InvitationTargetType, string> = {
  EXISTING_PROFESSIONAL: 'حساب مهني قائم', UNREGISTERED: 'بريد غير مسجَّل',
};

export const professionalKindLabel: Record<ProfessionalKind, string> = { SPECIALIST: 'أخصائي', COUNSELOR: 'مرشد طلابي', TEACHER: 'معلم' };
export const professionalStatusLabel: Record<ProfessionalVerificationStatus, string> = {
  UNVERIFIED: 'غير موثَّق', PENDING_IDENTITY_VERIFICATION: 'بانتظار توثيق الهوية',
  VERIFIED: 'موثَّق', DUPLICATE_SUSPECTED: 'اشتباه تكرار', MERGED: 'مدمج',
};
export const professionalStatusTone: Record<ProfessionalVerificationStatus, Tone> = {
  UNVERIFIED: 'neutral', PENDING_IDENTITY_VERIFICATION: 'warning',
  VERIFIED: 'success', DUPLICATE_SUSPECTED: 'danger', MERGED: 'neutral',
};
export const credentialStatusLabel: Record<CredentialStatus, string> = {
  UPLOADED: 'مرفوعة — بانتظار التحقق', VERIFIED: 'مُتحقَّق منها', EXPIRED: 'منتهية', HIDDEN: 'مخفية',
};
export const credentialStatusTone: Record<CredentialStatus, Tone> = {
  UPLOADED: 'neutral', VERIFIED: 'success', EXPIRED: 'warning', HIDDEN: 'neutral',
};

export const authorityTypeLabel: Record<string, string> = { STATUTORY_MANDATORY: 'جهة إلزامية', ELECTIVE_OPTIONAL: 'جهة اختيارية' };

/** أفعال سجل التدقيق — تُعرَض بالعربية مع الإبقاء على الكود الأصلي للبحث */
export const auditActionLabel: Record<string, string> = {
  'auth.login': 'تسجيل دخول',
  'auth.logout': 'تسجيل خروج',
  'auth.password_changed': 'تغيير كلمة المرور',
  'auth.refresh_reuse_detected': 'اشتباه سرقة جلسة — أُبطلت العائلة',
  'auth.refresh_retried': 'استعادة الجلسة بعد ضياع جواب التدوير',
  InstitutionCreated: 'إنشاء مؤسسة',
  InstitutionStatusChanged: 'تغيير حالة مؤسسة',
  InstitutionSettingsUpdated: 'تحديث إعدادات المؤسسة',
  UserProvisioned: 'إنشاء حساب',
  UserStatusChanged: 'تغيير حالة حساب',
  MembershipEnded: 'إنهاء ارتباط',
  InvitationSent: 'إرسال دعوة',
  InvitationAccepted: 'قبول دعوة',
  InvitationRejected: 'رفض دعوة',
  InvitationCancelled: 'إلغاء دعوة',
  ClarificationRequested: 'طلب توضيح على دعوة',
  ProfessionalRegistered: 'تسجيل حساب مهني',
  ProfessionalVerified: 'توثيق حساب مهني',
  CredentialUploaded: 'رفع شهادة',
  CredentialRemoved: 'إزالة شهادة',
  StudentDuplicateChecked: 'فحص تكرار طالب',
  StudentIdentityCreated: 'إنشاء هوية طالب',
  StudentEnrolled: 'التحاق طالب',
  StudentUpdated: 'تعديل بيانات طالب',
  GradePromoted: 'ترقية صف',
  SpecialEducationFileOpened: 'فتح ملف تربية خاصة',
  GuardianInvited: 'دعوة ولي أمر',
  GuardianActivated: 'تفعيل ولي أمر',
  StudentAccountCreated: 'إنشاء حساب طالب',
  StudentAccountActivated: 'تفعيل حساب طالب',
  StudentAccountPasswordReset: 'إعادة ضبط كلمة مرور حساب طالب',
  ConsentRequested: 'طلب موافقة',
  ConsentSigned: 'توقيع موافقة',
  ConsentRevoked: 'سحب موافقة',
  SensitiveDataViewed: 'قراءة بيانات حساسة',
  PlanCreated: 'إنشاء خطة',
  PlanSubmitted: 'إرسال خطة للمراجعة',
  PlanFirstReviewed: 'مراجعة أولى للخطة',
  PlanReturnedForEdit: 'إرجاع خطة للتعديل',
  PlanApproved: 'اعتماد خطة',
  PlanArchived: 'أرشفة خطة',
  PlanVersionCreated: 'نسخة جديدة من الخطة',
  GoalCreated: 'إنشاء هدف',
  GoalSubmitted: 'إرسال هدف للمراجعة',
  GoalApproved: 'اعتماد هدف',
  GoalReturnedForModification: 'إرجاع هدف للتعديل',
  GoalActivated: 'تفعيل هدف',
  GoalProgressStarted: 'بدء تنفيذ هدف',
  GoalPartiallyAchieved: 'تحقق جزئي لهدف',
  GoalCriteriaMet: 'تحقق معيار نجاح',
  GoalCriterionConfirmed: 'مصادقة على معيار نجاح',
  GoalToStabilization: 'انتقال هدف للتثبيت',
  GoalToGeneralization: 'انتقال هدف للتعميم',
  GoalClosed: 'إغلاق هدف',
  GoalModificationRequested: 'طلب تعديل هدف',
  GoalReturnedToDraft: 'إعادة هدف للمسودة',
  GoalExceptionRequested: 'طلب استثناء انتقال',
  GoalExceptionApproved: 'اعتماد استثناء انتقال',
  GoalExceptionRejected: 'رفض استثناء انتقال',
  GoalResumed: 'استئناف هدف موقوف',
  PreparationCreated: 'إنشاء تحضير يومي',
  PreparationCompleted: 'إكمال تحضير',
  SessionScheduled: 'جدولة جلسة',
  SessionPreparationAttached: 'ربط تحضير بجلسة',
  SessionReady: 'تجهيز جلسة',
  SessionStarted: 'بدء جلسة',
  SessionInterrupted: 'انقطاع جلسة',
  SessionResumed: 'استئناف جلسة',
  SessionEnded: 'إنهاء جلسة',
  SessionUploadStarted: 'بدء رفع تسجيل',
  SessionSaved: 'حفظ جلسة',
  SessionResultsRecorded: 'تسجيل نتائج أهداف',
  SessionResultsSubmitted: 'إرسال نتائج للمراجعة',
  SessionCompleted: 'إغلاق جلسة',
  SessionCancelled: 'إلغاء جلسة',
  RecordingStarted: 'بدء تسجيل',
  RecordingEnded: 'إنهاء تسجيل',
  RecordingSegmentUploaded: 'رفع مقطع تسجيل',
  RecordingStored: 'تخزين تسجيل بعد فحص التكامل',
  RecordingUploadFailed: 'فشل رفع تسجيل',
  RecordingDeleted: 'حذف تسجيل بعد انقضاء المدة',
  RecordingRetainedAnonymized: 'احتفاظ بتسجيل مجهَّل الهوية',
  RecordingAccessRequested: 'طلب اطلاع على تسجيل',
  RecordingAccessApproved: 'اعتماد اطلاع على تسجيل',
  RecordingAccessRejected: 'رفض اطلاع على تسجيل',
  RecordingAccessRevoked: 'سحب اطلاع على تسجيل',
  // الهوية والملفات والمقاعد — أسماء الأفعال الجديدة، وإلا ظهرت خاماً في سجل التدقيق
  InstitutionBrandingUpdated: 'تعديل البطاقة التعريفية',
  InstitutionLogoChanged: 'تغيير شعار المؤسسة',
  InstitutionLogoRemoved: 'إزالة شعار المؤسسة',
  InstitutionGalleryItemAdded: 'إضافة صورة إلى الألبوم',
  InstitutionGalleryItemUpdated: 'تعديل صورة في الألبوم',
  InstitutionGalleryItemRemoved: 'حذف صورة من الألبوم',
  MembershipProfileUpdated: 'تعديل المسمى المهني على العضوية',
  StudentPhotoChanged: 'تغيير صورة الطالب',
  StudentPhotoRemoved: 'حذف صورة الطالب',
  ProfessionalPhotoChanged: 'تغيير صورة الحساب المهني',
  ProfessionalPhotoRemoved: 'حذف صورة الحساب المهني',
  SeatRequestCreated: 'طلب زيادة مقاعد',
  SeatRequestApproved: 'اعتماد زيادة المقاعد',
  SeatRequestRejected: 'عدم اعتماد زيادة المقاعد',
  InvoiceIssued: 'إصدار فاتورة',
  PlansSeeded: 'بذر الباقات',
};

export function labelAudit(action: string): string {
  return auditActionLabel[action] ?? action;
}


/* ═══════════════════ الخطط والأهداف ═══════════════════ */

export const planTypeLabel: Record<PlanType, string> = {
  ANNUAL: 'خطة سنوية', SEMESTER: 'خطة فصلية', MONTHLY: 'خطة شهرية', FAMILY: 'خطة أسرية',
};
export const planStatusLabel: Record<PlanStatus, string> = {
  DRAFT: 'مسودة',
  SUBMITTED: 'مُرسَلة للمراجعة',
  FIRST_REVIEW: 'قيد المراجعة الأولى',
  RETURNED_FOR_EDIT: 'مُرجَعة للتعديل',
  PENDING_FINAL_APPROVAL: 'بانتظار الاعتماد النهائي',
  APPROVED: 'معتمدة',
  ARCHIVED: 'مؤرشفة',
};
export const planStatusTone: Record<PlanStatus, Tone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'brand',
  FIRST_REVIEW: 'brand',
  RETURNED_FOR_EDIT: 'warning',
  PENDING_FINAL_APPROVAL: 'brand',
  APPROVED: 'success',
  ARCHIVED: 'neutral',
};

export const goalStatusLabel: Record<GoalStatus, string> = {
  DRAFT: 'مسودة',
  PENDING_REVIEW: 'بانتظار المراجعة',
  APPROVED: 'معتمد — بانتظار التفعيل',
  ACTIVE: 'نشط',
  IN_PROGRESS: 'قيد التنفيذ',
  PARTIALLY_ACHIEVED: 'متحقق جزئياً',
  CRITERIA_MET: 'تحقق معيار النجاح',
  STABILIZATION: 'مرحلة التثبيت',
  GENERALIZATION: 'مرحلة التعميم',
  NEEDS_MODIFICATION: 'يحتاج تعديلاً',
  PAUSED: 'موقوف باستثناء معتمد',
  CLOSED: 'مغلق',
};
/** خريطة الحالة إلى اللون — القسم 04 من دليل الهوية، ملزمة */
export const goalStatusTone: Record<GoalStatus, Tone> = {
  DRAFT: 'neutral',
  PENDING_REVIEW: 'neutral',
  APPROVED: 'brand',
  ACTIVE: 'brand',
  IN_PROGRESS: 'brand',
  PARTIALLY_ACHIEVED: 'brand',
  CRITERIA_MET: 'success',
  STABILIZATION: 'brand',
  GENERALIZATION: 'brand',
  NEEDS_MODIFICATION: 'warning',
  PAUSED: 'neutral',
  CLOSED: 'success',
};

export const exceptionStatusLabel: Record<ExceptionRequestStatus, string> = {
  PENDING: 'بانتظار قرار المشرف', APPROVED: 'استثناء معتمد', REJECTED: 'استثناء مرفوض',
};
export const exceptionStatusTone: Record<ExceptionRequestStatus, Tone> = {
  PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger',
};

export const goalTriggerLabel: Record<string, string> = {
  create: 'إنشاء الهدف',
  submit: 'إرسال للمراجعة',
  approve: 'اعتماد المشرف',
  return_for_modification: 'إرجاع للتعديل',
  activate: 'تفعيل',
  start_progress: 'بدء التنفيذ',
  mark_partial: 'تحقق جزئي',
  mark_criteria_met: 'إعلان تحقق المعيار',
  to_stabilization: 'الانتقال للتثبيت',
  to_generalization: 'الانتقال للتعميم',
  close: 'إغلاق',
  request_modification: 'طلب تعديل',
  resubmit_after_modification: 'إعادة للمسودة',
  pause_by_exception: 'إيقاف باستثناء معتمد',
  resume: 'استئناف',
};


/* ═══════════════════ التحضير والجلسات والتسجيلات ═══════════════════ */

export const preparationStatusLabel: Record<PreparationStatus, string> = { DRAFT: 'مسودة', COMPLETE: 'مكتمل' };
export const preparationStatusTone: Record<PreparationStatus, Tone> = { DRAFT: 'warning', COMPLETE: 'success' };

export const sessionStatusLabel: Record<SessionStatus, string> = {
  SCHEDULED: 'مجدولة',
  READY: 'جاهزة للبدء',
  ACTIVE: 'جارية الآن',
  INTERRUPTED: 'انقطع الاتصال',
  ENDED: 'منتهية',
  UPLOADING: 'جارٍ رفع التسجيل',
  SAVED: 'محفوظة',
  NEEDS_REVIEW: 'بانتظار مراجعة المشرف',
  COMPLETED: 'مكتملة',
  CANCELLED: 'ملغاة',
};
export const sessionStatusTone: Record<SessionStatus, Tone> = {
  SCHEDULED: 'neutral',
  READY: 'brand',
  ACTIVE: 'brand',
  INTERRUPTED: 'warning',
  ENDED: 'brand',
  UPLOADING: 'brand',
  SAVED: 'brand',
  NEEDS_REVIEW: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
};
export const sessionLocationLabel: Record<SessionLocation, string> = { ONSITE: 'حضورية', ONLINE: 'عن بُعد' };

export const goalResultLabel: Record<GoalResultStatus, string> = {
  ACHIEVED: 'تحقق', PARTIAL: 'تحقق جزئياً', NOT_ACHIEVED: 'لم يتحقق', NOT_ATTEMPTED: 'لم يُنفَّذ',
};
export const goalResultTone: Record<GoalResultStatus, Tone> = {
  ACHIEVED: 'success', PARTIAL: 'brand', NOT_ACHIEVED: 'warning', NOT_ATTEMPTED: 'neutral',
};

export const recordingStatusLabel: Record<RecordingStatus, string> = {
  PENDING: 'بدأ مع الجلسة',
  UPLOADING: 'جارٍ الرفع',
  STORED: 'مخزَّن ومطابق للتكامل',
  UPLOAD_FAILED: 'فشل الرفع',
  DELETED: 'حُذف بعد انقضاء المدة',
  RETAINED_ANONYMIZED: 'محتفَظ به مجهَّل الهوية',
};
export const recordingStatusTone: Record<RecordingStatus, Tone> = {
  PENDING: 'neutral', UPLOADING: 'brand', STORED: 'success',
  UPLOAD_FAILED: 'danger', DELETED: 'neutral', RETAINED_ANONYMIZED: 'brand',
};

export const callRecordingStatusLabel: Record<CallRecordingStatus, string> = {
  NONE: 'بلا تسجيل', RECORDING: 'يُسجَّل', PROCESSING: 'ينتظر الضغط', READY: 'جاهز', FAILED: 'تعذّر', DELETED: 'مُحي',
};
export const callRecordingStatusTone: Record<CallRecordingStatus, Tone> = {
  NONE: 'neutral', RECORDING: 'brand', PROCESSING: 'warning', READY: 'success', FAILED: 'danger', DELETED: 'neutral',
};

export const recordingAccessLabel: Record<RecordingAccessStatus, string> = {
  REQUESTED: 'طلب جديد', UNDER_REVIEW: 'قيد المراجعة', APPROVED: 'ممنوح مؤقتاً',
  REJECTED: 'مرفوض', EXPIRED: 'انتهت مدته', REVOKED: 'مسحوب',
};

export const sessionTriggerLabel: Record<string, string> = {
  schedule: 'جدولة', mark_ready: 'تجهيز', start: 'بدء', interrupt: 'انقطاع',
  resume: 'استئناف', end: 'إنهاء', begin_upload: 'بدء الرفع', finish_upload: 'حفظ',
  submit_results: 'إرسال النتائج', complete: 'إغلاق بعد المراجعة', cancel: 'إلغاء',
};

/* ═══════════════════ التكامل ═══════════════════ */

export const transportLabel: Record<IntegrationTransport, string> = {
  NONE: 'بلا ناقل — لا شيء يغادر المنصة',
  MANUAL_EXPORT: 'تصدير يدوي',
  WEBHOOK: 'ربط شبكي',
  SFTP: 'نقل ملفات آمن',
};
export const outboxStatusLabel: Record<OutboxStatus, string> = {
  PENDING: 'بانتظار التجهيز', READY_FOR_EXPORT: 'جاهز للتصدير',
  SENT: 'أُرسل', SKIPPED: 'متجاوَز', FAILED: 'فشل',
};
export const outboxStatusTone: Record<OutboxStatus, Tone> = {
  PENDING: 'neutral', READY_FOR_EXPORT: 'brand', SENT: 'success', SKIPPED: 'neutral', FAILED: 'danger',
};
export const outboxSkipReason: Record<string, string> = {
  NO_FIELD_MAP: 'لا خريطة حقول لهذا الحدث — لم يخرج شيء',
  FILTERED_OUT: 'استُبعد بالمرشِّحات',
  ENTITY_GONE: 'الكيان لم يعد موجوداً',
  TRANSPORT_NOT_CONFIGURED: 'الناقل غير مضبوط بعد — ينتظر نموذج التكامل',
};

/* ═══════════════════ P4 ═══════════════════ */

export const reportStatusLabel: Record<ReportStatus, string> = {
  DRAFT: 'مسودة',
  PENDING_REVIEW: 'بانتظار المراجعة',
  RETURNED_FOR_MODIFICATION: 'مُرجَع للتعديل',
  REVIEWED: 'روجِع — بانتظار الاعتماد',
  APPROVED: 'معتمد',
  PUBLISHED: 'منشور للأسرة',
  ARCHIVED: 'مؤرشف',
  CANCELLED: 'ملغى',
};
export const reportStatusTone: Record<ReportStatus, Tone> = {
  DRAFT: 'neutral', PENDING_REVIEW: 'warning', RETURNED_FOR_MODIFICATION: 'danger',
  REVIEWED: 'brand', APPROVED: 'success', PUBLISHED: 'success',
  ARCHIVED: 'neutral', CANCELLED: 'neutral',
};
export const reportKindLabel: Record<ReportKind, string> = {
  SPECIAL_EDUCATION: 'تربية خاصة',
  COUNSELING: 'إرشاد',
};

export const referralTrackLabel: Record<ReferralTargetTrack, string> = {
  COUNSELING: 'إرشاد',
  SPECIAL_EDUCATION: 'تربية خاصة',
};
export const referralSourceLabel: Record<ReferralSource, string> = {
  PARENT: 'وليّ الأمر',
  STUDENT: 'الطالب نفسه',
  TEACHER_REFERRAL: 'معلّم',
  ADMIN_REFERRAL: 'أُدخلت نيابةً',
};
export const referralStatusLabel: Record<ReferralStatus, string> = {
  PENDING: 'بانتظار القرار',
  ACCEPTED: 'مقبولة',
  DECLINED: 'مرفوضة',
  CANCELLED: 'ملغاة',
};
export const referralStatusTone: Record<ReferralStatus, Tone> = {
  PENDING: 'warning', ACCEPTED: 'success', DECLINED: 'danger', CANCELLED: 'neutral',
};

export const observationDomainLabel: Record<ObservationDomain, string> = {
  SOCIAL: 'اجتماعي',
  ACADEMIC: 'أكاديمي',
  EMOTIONAL: 'انفعالي',
  BEHAVIORAL: 'سلوكي/انضباطي',
};
export const observationLevelLabel: Record<ObservationLevel, string> = {
  NO_CONCERN: 'لا ملاحظة',
  MILD: 'خفيف',
  MODERATE: 'متوسط',
  SEVERE: 'شديد',
};
export const observationLevelTone: Record<ObservationLevel, Tone> = {
  NO_CONCERN: 'success', MILD: 'neutral', MODERATE: 'warning', SEVERE: 'danger',
};

/** خانات فرعية مقترحة لكل تصنيف — المؤسسة تخصّصها لاحقاً عبر بناء النماذج */
export const observationIndicators: Record<ObservationDomain, string[]> = {
  SOCIAL: ['انسحاب', 'صعوبة تكوين صداقات', 'مشاركة في الأنشطة'],
  ACADEMIC: ['تركيز', 'إنجاز الواجبات', 'مستوى القراءة'],
  EMOTIONAL: ['قلق', 'تقلّب المزاج', 'ثقة بالنفس'],
  BEHAVIORAL: ['تنمّر', 'سرقة', 'عدوانية', 'التزام بالأنظمة'],
};

/* ═══════════════════ P5 ═══════════════════ */

export const ruleSeverityLabel: Record<RuleSeverity, string> = {
  LOW: 'منخفض', MEDIUM: 'متوسط', HIGH: 'مرتفع', CRITICAL: 'حرج',
};
export const ruleSeverityTone: Record<RuleSeverity, Tone> = {
  LOW: 'neutral', MEDIUM: 'brand', HIGH: 'warning', CRITICAL: 'danger',
};
export const ruleActionLabel: Record<RuleAction, string> = {
  BLOCK: 'رفض العملية', RAISE_ALERT: 'رفع تنبيه', RECALCULATE: 'إعادة حساب',
};
export const ruleKindLabel: Record<RuleKind, string> = {
  ON_ATTEMPT: 'عند محاولة العملية', SCHEDULED: 'تمريرة مجدولة',
};
export const alertStatusLabel: Record<AlertStatus, string> = {
  OPEN: 'مفتوح', ACKNOWLEDGED: 'قيد المعالجة', RESOLVED: 'مُعالَج', DISMISSED: 'مُستبعَد',
};
export const alertStatusTone: Record<AlertStatus, Tone> = {
  OPEN: 'warning', ACKNOWLEDGED: 'brand', RESOLVED: 'success', DISMISSED: 'neutral',
};
export const indicatorLabel: Record<IndicatorKind, string> = {
  QUALITY: 'مؤشر الجودة', PROTECTION: 'مؤشر الحماية',
};
export const criterionStateTone: Record<'ok' | 'warning' | 'critical', Tone> = {
  ok: 'success', warning: 'warning', critical: 'danger',
};
export const criticalIncidentLabel: Record<string, string> = {
  RECORDING_WITHOUT_CONSENT: 'تسجيل بلا موافقة سارية',
  RECORDING_VIEWED_WITHOUT_PERMISSION: 'مشاهدة تسجيل بلا صلاحية',
  RETENTION_EXCEEDED: 'تجاوز مدة حذف التسجيل',
  INTERNAL_NOTE_EXPOSED: 'كشف ملاحظة داخلية',
  REPORT_SHARED_UNAUTHORIZED: 'مشاركة تقرير مع جهة غير مصرَّح لها',
};

export const counselingPlanStatusLabel: Record<CounselingPlanStatus, string> = {
  DRAFT: 'مسودة', ACTIVE: 'نشطة', COMPLETED: 'مكتملة', ARCHIVED: 'مؤرشفة',
};
export const counselingPlanStatusTone: Record<CounselingPlanStatus, Tone> = {
  DRAFT: 'neutral', ACTIVE: 'success', COMPLETED: 'brand', ARCHIVED: 'neutral',
};
export const counselingSessionStatusLabel: Record<CounselingSessionStatus, string> = {
  SCHEDULED: 'مجدولة', ACTIVE: 'جارية', ENDED: 'انتهت', DOCUMENTED: 'موثَّقة', CANCELLED: 'ملغاة',
};
export const counselingSessionStatusTone: Record<CounselingSessionStatus, Tone> = {
  SCHEDULED: 'neutral', ACTIVE: 'warning', ENDED: 'brand', DOCUMENTED: 'success', CANCELLED: 'neutral',
};
export const counselingModeLabel: Record<CounselingSessionMode, string> = {
  INDIVIDUAL: 'فردية', GROUP: 'جماعية', FAMILY: 'أسرية', ONLINE: 'عن بُعد',
};
export const recommendationAudienceLabel: Record<RecommendationAudience, string> = {
  STUDENT: 'للطالب', PARENT: 'لوليّ الأمر', MANAGEMENT: 'للإدارة',
};
export const recommendationAudienceTone: Record<RecommendationAudience, Tone> = {
  STUDENT: 'brand', PARENT: 'success', MANAGEMENT: 'neutral',
};

/* ── P6 ── */
export const broadcastStatusLabel: Record<string, string> = {
  SCHEDULED: 'مجدولة', LIVE: 'جارية الآن', ENDED: 'انتهت', CANCELLED: 'ألغيت', BLOCKED: 'مُنعت',
};
export const broadcastStatusTone: Record<string, Tone> = {
  SCHEDULED: 'brand', LIVE: 'success', ENDED: 'neutral', CANCELLED: 'neutral', BLOCKED: 'danger',
};
export const viewerScopeLabel: Record<string, string> = {
  CLASS_GUARDIANS: 'أولياء أمور طلبة الصف',
  CLASS_GUARDIANS_AND_REGULATOR: 'أولياء أمور طلبة الصف + الجهة الرقابية',
};
export const broadcastProviderLabel: Record<string, string> = {
  NONE: 'بلا ناقل — يُجهَّز ولا يُبثّ',
  LIVEKIT: 'LiveKit',
};
export const billingPeriodLabel: Record<string, string> = { MONTHLY: 'شهرياً', TERM: 'للفصل الدراسي', YEARLY: 'سنوياً' };
export const subscriptionStatusLabel: Record<string, string> = { TRIAL: 'تجريبي', ACTIVE: 'ساري', PAST_DUE: 'متأخر السداد', SUSPENDED: 'موقوف', CANCELLED: 'ملغى' };
export const subscriptionStatusTone: Record<string, Tone> = { TRIAL: 'brand', ACTIVE: 'success', PAST_DUE: 'warning', SUSPENDED: 'danger', CANCELLED: 'neutral' };
export const invoiceStatusLabel: Record<string, string> = { DRAFT: 'مسودة', ISSUED: 'صادرة', PAID: 'مدفوعة', FAILED: 'فشل السداد', REFUNDED: 'مستردّة', VOID: 'ملغاة' };
export const invoiceStatusTone: Record<string, Tone> = { DRAFT: 'neutral', ISSUED: 'brand', PAID: 'success', FAILED: 'danger', REFUNDED: 'warning', VOID: 'neutral' };
export const paymentProviderLabel: Record<string, string> = { NONE: 'بلا مزوّد — تُصدَر الفاتورة ولا يُحصَّل', PAYTABS: 'PayTabs', CLIQ: 'CliQ' };
export const currencyLabel: Record<string, string> = { JOD: 'دينار أردني', USD: 'دولار' };
export const reviewTypeLabel: Record<string, string> = { goal: 'هدف', plan: 'خطة', report: 'تقرير' };
export const certificateBandLabel: Record<string, string> = { EXCELLENT: 'متميّز', GOOD: 'جيد' };
export const jobStatusLabel: Record<string, string> = { OPEN: 'مفتوح', CLOSED: 'مغلق' };
export const applicationStatusLabel: Record<string, string> = { SUBMITTED: 'مقدَّم', SHORTLISTED: 'مرشَّح', INVITED: 'مدعوّ للانضمام', REJECTED: 'مرفوض', WITHDRAWN: 'مسحوب' };
export const applicationStatusTone: Record<string, Tone> = { SUBMITTED: 'brand', SHORTLISTED: 'warning', INVITED: 'success', REJECTED: 'danger', WITHDRAWN: 'neutral' };
export const continuityStatusLabel: Record<string, string> = { ISSUED: 'صادرة — بانتظار الاستيراد', IMPORTED: 'استُوردت', EXPIRED: 'انتهت', REVOKED: 'أُلغيت' };

/* ── طلبات زيادة المقاعد (P6) ── */
export const seatRequestStatusLabel: Record<SeatRequestStatus, string> = {
  PENDING: 'معلَّق — بانتظار إدارة المنصة', APPROVED: 'معتمَد وصدرت فاتورته', REJECTED: 'غير معتمَد',
};
export const seatRequestStatusTone: Record<SeatRequestStatus, Tone> = {
  PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger',
};

/* ── 0027 — وليّ الأمر أولاً ── */
export const linkRequestStatusLabel: Record<LinkRequestStatus, string> = {
  PENDING: 'معلَّق — بانتظار المؤسسة', ACCEPTED: 'مقبول — التحق', REJECTED: 'لم يُقبل', CANCELLED: 'ألغيتَه',
};
export const linkRequestStatusTone: Record<LinkRequestStatus, Tone> = {
  PENDING: 'warning', ACCEPTED: 'success', REJECTED: 'danger', CANCELLED: 'neutral',
};
export const guardianDocTypeLabel: Record<GuardianDocType, string> = {
  BIRTH_CERTIFICATE: 'شهادة ميلاد', PASSPORT: 'جواز سفر', UNHCR_CARD: 'بطاقة المفوضية (UNHCR)', NATIONAL_ID: 'هوية وطنية', OTHER: 'وثيقة أخرى',
};

/* ── 0027 — السجلّات والمعرفة ── */
export const studentFileKindLabel: Record<StudentFileKind, string> = {
  ACADEMIC: 'الملف الأكاديمي', BEHAVIORAL: 'الملف السلوكي',
  WEEKLY_PLAN: 'الخطة الأسبوعية', MONTHLY_PLAN: 'الخطة الشهرية', TERM_PLAN: 'خطة الفصل',
};
/** ما يُكتب في «اسم الفصل» لكل نوع — الدورة هي هويّة الخطة */
export const studentFileTermHint: Record<StudentFileKind, string> = {
  ACADEMIC: 'مثال: الفصل الأول 2026-2027', BEHAVIORAL: 'مثال: الفصل الأول 2026-2027',
  WEEKLY_PLAN: 'مثال: الأسبوع 12 — 2026/2027', MONTHLY_PLAN: 'مثال: تشرين الأول 2026',
  TERM_PLAN: 'مثال: الفصل الأول 2026-2027',
};
export const studentFileSpanLabel: Record<StudentFileSpan, string> = { SHORT_TERM: 'قصير المدى (فصلي)', CUMULATIVE: 'تراكمي' };
export const studentFileFieldLabel: Record<StudentFileField, string> = {
  performanceLevelAr: 'مستوى الأداء الحالي', strengthsAr: 'نقاط القوة', weaknessesAr: 'نقاط الضعف',
  iepAr: 'الخطة التربوية الفردية', educationPlanAr: 'الخطة التعليمية',
  planGoalsAr: 'أهداف الدورة', planActivitiesAr: 'أنشطة الدورة وإجراءاتها', planEvaluationAr: 'تقييم نهاية الدورة',
};
export const followupResultLabel: Record<FollowupResult, string> = { MASTERED: 'أتقن', NOT_MASTERED: 'لم يتقن', PARTIAL: 'جزئياً' };
export const followupResultTone: Record<FollowupResult, Tone> = { MASTERED: 'success', NOT_MASTERED: 'danger', PARTIAL: 'warning' };
export const assistanceLabel: Record<AssistanceType, string> = { NONE: 'بلا مساعدة', VERBAL: 'لفظية', GESTURAL: 'إيمائية', MODELING: 'نمذجة', PHYSICAL: 'جسدية' };
export const requiredEntityLabel: Record<RequiredFieldEntity, string> = {
  STUDENT_FILE: 'ملف الطالب', DAILY_FOLLOWUP: 'المتابعة اليومية', PLAN: 'الخطة', REPORT: 'التقرير',
};
/** مفاتيح الحقول القابلة للإلزام كما يعرفها الخادم — الاسم العربي للعرض */
export const requiredFieldKeyLabel: Record<string, string> = {
  ...studentFileFieldLabel,
  notesAr: 'ملاحظات اليوم', 'items.noteAr': 'ملاحظة على كل مهمة',
  title: 'عنوان الخطة', summary: 'الملخّص', recommendations: 'التوصيات',
};
export const newsKindLabel: Record<NewsKind, string> = { ACHIEVEMENT: 'إنجاز', ANNOUNCEMENT: 'إعلان' };
export const newsKindTone: Record<NewsKind, Tone> = { ACHIEVEMENT: 'success', ANNOUNCEMENT: 'brand' };

/* ── 0031 — المستفيد والاستشارات المدفوعة والمحفظة ── */
export const clientStatusLabel: Record<ClientStatus, string> = { ACTIVE: 'نشط', SUSPENDED: 'موقوف' };
export const clientStatusTone: Record<ClientStatus, Tone> = { ACTIVE: 'success', SUSPENDED: 'warning' };

export const callModeLabel: Record<CallMode, string> = {
  VOICE: 'صوت فقط', VIDEO: 'فيديو للطرفين', PROVIDER_VIDEO: 'كاميرا المهني وحدها',
};
export const callModeHint: Record<CallMode, string> = {
  VOICE: 'لا كاميرا من أيّ طرف — صوتٌ فقط.',
  VIDEO: 'كاميرا المستفيد وكاميرا المهني معاً.',
  PROVIDER_VIDEO: 'تَرى المهني ولا يراك — كاميرتُك مغلقة.',
};

export const consultationStatusLabel: Record<ConsultationStatus, string> = {
  REQUESTED: 'طلبٌ بانتظار القبول',
  ACCEPTED: 'مقبولة — بانتظار موعدها',
  DECLINED: 'لم تُقبل',
  IN_PROGRESS: 'جارية الآن',
  COMPLETED: 'تمّت',
  CANCELLED: 'ألغاها المستفيد',
  NO_SHOW: 'لم يحضر المستفيد',
};
export const consultationStatusTone: Record<ConsultationStatus, Tone> = {
  REQUESTED: 'warning', ACCEPTED: 'brand', DECLINED: 'danger', IN_PROGRESS: 'success',
  COMPLETED: 'success', CANCELLED: 'neutral', NO_SHOW: 'danger',
};

/** اسم كل إجراء كما يقرؤه صاحبه — والخريطة في consultation-state.ts هي الحَكَم */
export const consultationTriggerLabel: Record<ConsultationTrigger, string> = {
  accept: 'اقبل الطلب', decline: 'اعتذر عن الطلب', cancel: 'ألغِ الحجز',
  start: 'ابدأ الاستشارة', end: 'أنهِ الاستشارة', no_show: 'لم يحضر المستفيد',
};

export const walletOwnerKindLabel: Record<WalletOwnerKind, string> = { CLIENT: 'محفظة مستفيد', PROFESSIONAL: 'محفظة مهني' };

export const walletEntryKindLabel: Record<WalletEntryKind, string> = {
  TOPUP: 'شحن رصيد',
  HOLD: 'حجزٌ لاستشارة',
  HOLD_RELEASE: 'فكُّ حجز',
  CHARGE: 'خصمُ استشارة',
  EARNING: 'أتعاب استشارة',
  PLATFORM_FEE: 'رسم المنصة',
  REFUND: 'ردُّ مبلغ',
  PAYOUT: 'سحبٌ مطلوب',
  PAYOUT_REVERSAL: 'إعادة مبلغ سحبٍ مرفوض',
};
export const walletEntryKindTone: Record<WalletEntryKind, Tone> = {
  TOPUP: 'success', HOLD: 'warning', HOLD_RELEASE: 'neutral', CHARGE: 'danger',
  EARNING: 'success', PLATFORM_FEE: 'warning', REFUND: 'success', PAYOUT: 'brand', PAYOUT_REVERSAL: 'success',
};

export const payoutStatusLabel: Record<PayoutStatus, string> = {
  PENDING: 'معلَّق — بانتظار القرار', APPROVED: 'معتمَد', REJECTED: 'مرفوض — أُعيد المبلغ', PAID: 'صُرف',
};
export const payoutStatusTone: Record<PayoutStatus, Tone> = {
  PENDING: 'warning', APPROVED: 'brand', REJECTED: 'danger', PAID: 'success',
};

export const complaintStatusLabel: Record<ComplaintStatus, string> = {
  OPEN: 'مفتوحة', UNDER_REVIEW: 'قيد المراجعة', RESOLVED: 'حُسمت لصالح الشاكي', REJECTED: 'لم تُقبل',
};
export const complaintStatusTone: Record<ComplaintStatus, Tone> = {
  OPEN: 'warning', UNDER_REVIEW: 'brand', RESOLVED: 'success', REJECTED: 'danger',
};

/* ── 0032 · 0033 — التسجيل الأكاديمي والأكاديميات والدورات ─────────────── */

export const providerApplicationStatusLabel: Record<ProviderApplicationStatus, string> = {
  PENDING: 'قيد الدراسة', APPROVED: 'معتمَد — يمكنك عرض دوراتك', REJECTED: 'لم يُعتمد',
};
export const providerApplicationStatusTone: Record<ProviderApplicationStatus, Tone> = {
  PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger',
};

export const academyStatusLabel: Record<AcademyStatus, string> = {
  PENDING: 'بانتظار اعتماد إدارة المنصة',
  ACTIVE: 'معتمَدة',
  SUSPENDED: 'موقوفة',
  REJECTED: 'لم تُعتمد',
};
export const academyStatusTone: Record<AcademyStatus, Tone> = {
  PENDING: 'warning', ACTIVE: 'success', SUSPENDED: 'warning', REJECTED: 'danger',
};

/** الأكاديميةُ مجموعةً (0057): بابُ المنشور وحالتُه */
export const academyPostAccessLabel: Record<AcademyPostAccess, string> = { PUBLIC: 'عام', MEMBERS: 'للأعضاء', PAID: 'مدفوع' };
export const academyPostStatusLabel: Record<AcademyPostStatus, string> = { DRAFT: 'مسوّدة', PUBLISHED: 'منشور', ARCHIVED: 'مؤرشف' };
export const academyPostStatusTone: Record<AcademyPostStatus, Tone> = { DRAFT: 'warning', PUBLISHED: 'success', ARCHIVED: 'neutral' };

export const academyMemberStatusLabel: Record<AcademyMemberStatus, string> = {
  INVITED: 'دعوةٌ بانتظار ردّه', ACTIVE: 'عضوٌ نشط', ENDED: 'انتهت العضوية',
};
export const academyMemberStatusTone: Record<AcademyMemberStatus, Tone> = {
  INVITED: 'warning', ACTIVE: 'success', ENDED: 'neutral',
};

export const courseStatusLabel: Record<CourseStatus, string> = {
  DRAFT: 'مسوّدة — لا يراها أحد',
  PUBLISHED: 'منشورة — معروضة للتسجيل',
  CLOSED: 'مغلقة — لا تقبل تسجيلاً جديداً',
  CANCELLED: 'ملغاة',
};
export const courseStatusTone: Record<CourseStatus, Tone> = {
  DRAFT: 'neutral', PUBLISHED: 'success', CLOSED: 'warning', CANCELLED: 'danger',
};

export const courseDeliveryLabel: Record<CourseDelivery, string> = {
  ONLINE: 'عن بُعد', ONSITE: 'حضورية', HYBRID: 'مدمجة',
};
export const courseAudienceLabel: Record<CourseAudience, string> = {
  PUBLIC: 'للجميع', GUARDIANS: 'لأولياء الأمور', PROFESSIONALS: 'للمختصين',
};
export const courseCertificateLabel: Record<CourseCertificate, string> = {
  NONE: 'بلا شهادة', ATTENDANCE: 'شهادة حضور', ACCREDITED: 'شهادة معتمدة من رفرفة',
};
export const courseCertificateHint: Record<CourseCertificate, string> = {
  NONE: 'يُسجَّل إتمام المتدرّب بلا إصدار شهادة.',
  ATTENDANCE: 'شهادةٌ برمزٍ يُتحقَّق منه علناً، تشهد بالحضور لا بالاعتماد.',
  ACCREDITED: 'شهادةٌ برمزٍ يُتحقَّق منه علناً، تحمل اسم الأكاديمية ورقمها المرجعي.',
};

export const enrollmentStatusCLabel: Record<EnrollmentStatusC, string> = {
  ENROLLED: 'مسجَّل', COMPLETED: 'أتمّها', CANCELLED: 'أُلغي التسجيل',
};
export const enrollmentStatusCTone: Record<EnrollmentStatusC, Tone> = {
  ENROLLED: 'brand', COMPLETED: 'success', CANCELLED: 'neutral',
};

/**
 * وسمُ المستفيد للعرض (0034). الاسمُ صار اختيارياً لأن المنصة لا تسأل عنه —
 * فالرقمُ المرجعي هو ما يُعرَّف به، والاسمُ يُعرض إن كتبه صاحبُه وحده.
 * وبدونها كان القالب يطبع «شكوى null» حرفياً على الشاشة.
 */
export const beneficiaryLabel = (c: { fullName: string | null; referenceCode: string }): string =>
  c.fullName?.trim() || `مستفيد ${c.referenceCode}`;

/** نوعُ المساعدة كما سمّته صاحبةُ المنتج (20 سبتمبر 2026) */
export const promptLevelLabel: Record<import('./types').PromptLevel, string> = {
  INDEPENDENT: 'مستقل',
  VERBAL: 'تلميح لفظي',
  GESTURAL: 'تلميح إيمائي',
  PARTIAL_PHYSICAL: 'مساعدة جسدية جزئية',
  FULL_PHYSICAL: 'مساعدة جسدية كاملة',
};

/* ── 0066 — ملاحظاتُ التحسين ── */
export const noteTargetLabel: Record<string, string> = { ACADEMY: 'أكاديمية', INSTITUTION: 'مؤسسة', PROFESSIONAL: 'حساب مهني', COURSE: 'دورة' };
export const noteSeverityLabel: Record<string, string> = { INFO: 'للعلم', SUGGESTION: 'اقتراح', ACTION_REQUIRED: 'يلزمه إجراء' };
export const noteSeverityTone: Record<string, Tone> = { INFO: 'neutral', SUGGESTION: 'brand', ACTION_REQUIRED: 'warning' };
export const noteStatusLabel: Record<string, string> = { OPEN: 'مفتوحة', ACKNOWLEDGED: 'اطّلع عليها صاحبها', RESOLVED: 'عولجت', CLOSED: 'مغلقة' };
export const noteStatusTone: Record<string, Tone> = { OPEN: 'warning', ACKNOWLEDGED: 'brand', RESOLVED: 'success', CLOSED: 'neutral' };

/** سببُ تغيير موعد الجلسة (0084) */
export const rescheduleReasonLabel: Record<import('./types').RescheduleReason, string> = {
  ABSENCE: 'غياب الطالب',
  MAKEUP: 'جلسة تعويضية',
  INTENSIVE: 'تكثيف',
  OTHER: 'سبب آخر',
};

